(() => {
  if (window.MyPCProductExperienceV1) return;
  window.MyPCProductExperienceV1 = true;

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const hydrateVideo = (video) => {
    if (!video || video.dataset.mypcHydrated === 'true') return;
    let changed = false;
    $$('source[data-src]', video).forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      changed = true;
    });
    video.dataset.mypcHydrated = 'true';
    if (changed) video.load();
  };

  const safelyPlay = (video) => {
    if (!video) return;
    hydrateVideo(video);
    video.muted = true;
    video.defaultMuted = true;
    const promise = video.play();
    promise?.catch(() => {
      // Safari, ahorro de energía o ahorro de datos pueden bloquear autoplay.
    });
  };

  const closeLightbox = (dialog) => {
    if (!dialog || dialog.hidden) return;
    const player = $('[data-mypc-video-lightbox-player]', dialog);
    const sourceVideo = dialog.mypcSourceVideo;
    const opener = dialog.mypcOpener;

    dialog.classList.remove('is-open');
    dialog.setAttribute('aria-hidden', 'true');
    player?.pause();
    document.body.classList.remove('mypc-video-lightbox-open');

    window.setTimeout(() => {
      dialog.hidden = true;
      if (player) {
        player.removeAttribute('src');
        player.replaceChildren();
        player.load();
      }
      if (sourceVideo?.closest('[data-media-item]')?.classList.contains('is-active')) {
        safelyPlay(sourceVideo);
      }
      opener?.focus?.({ preventScroll: true });
      dialog.mypcSourceVideo = null;
      dialog.mypcOpener = null;
    }, reducedMotion() ? 0 : 320);
  };

  const openLightbox = (sourceVideo, opener) => {
    const dialog = $('[data-mypc-video-lightbox-dialog]');
    const player = dialog && $('[data-mypc-video-lightbox-player]', dialog);
    if (!dialog || !player || !sourceVideo) return;

    hydrateVideo(sourceVideo);
    const sources = $$('source', sourceVideo)
      .map((source) => ({ src: source.src || source.dataset.src, type: source.type }))
      .filter((source) => source.src);
    if (!sources.length) return;

    player.replaceChildren(...sources.map((sourceData) => {
      const source = document.createElement('source');
      source.src = sourceData.src;
      if (sourceData.type) source.type = sourceData.type;
      return source;
    }));
    player.poster = sourceVideo.poster || '';
    player.muted = true;
    player.defaultMuted = true;
    player.loop = true;
    dialog.mypcSourceVideo = sourceVideo;
    dialog.mypcOpener = opener;

    sourceVideo.pause();
    dialog.hidden = false;
    dialog.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mypc-video-lightbox-open');
    player.load();

    const resumeAt = Number.isFinite(sourceVideo.currentTime) ? sourceVideo.currentTime : 0;
    player.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(resumeAt) && resumeAt < player.duration) player.currentTime = resumeAt;
      safelyPlay(player);
    }, { once: true });

    window.requestAnimationFrame(() => {
      dialog.classList.add('is-open');
      $('[data-mypc-video-lightbox-close]', dialog)?.focus({ preventScroll: true });
    });
  };

  const initializeLightbox = () => {
    const dialog = $('[data-mypc-video-lightbox-dialog]');
    if (!dialog || dialog.dataset.mypcInitialized === 'true') return;
    dialog.dataset.mypcInitialized = 'true';

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-mypc-video-lightbox-close]')) {
        closeLightbox(dialog);
      }
    });
  };

  const initializeProductMedia = (scope = document) => {
    $$('[data-mypc-product-media]', scope).forEach((root) => {
      if (root.dataset.mypcInitialized === 'true') return;
      const stage = $('[data-mypc-media-stage]', root);
      if (!stage) return;

      root.dataset.mypcInitialized = 'true';
      const items = $$('[data-media-item]', root);
      const thumbs = $$('[data-media-thumb]', root);
      const maxHeightPercent = Math.max(55, Math.min(98, Number(root.dataset.mypcVideoMaxHeight || 78)));
      const transitionDuration = Math.max(0, Number(root.dataset.mypcMediaTransition || 300));
      const verticalFit = root.dataset.mypcVerticalFit || 'soft_crop';
      let galleryVisible = true;
      let resizeFrame = null;
      let swipeStart = null;
      let suppressVideoOpen = false;

      const firstMainVideo = $('[data-mypc-video-item]', root);
      if (firstMainVideo) {
        items.forEach((item) => item.classList.toggle('is-active', item === firstMainVideo));
        thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaThumb === firstMainVideo.dataset.mediaItem));
      }

      stage.style.transitionDuration = `${transitionDuration}ms`;

      const getActiveItem = () => $('[data-media-item].is-active', root) || items[0];

      $$('[data-mypc-main-video]', root).forEach((video) => {
        video.addEventListener('error', () => {
          const failedItem = video.closest('[data-media-item]');
          if (!failedItem || failedItem.dataset.mypcMediaFailed === 'true') return;
          failedItem.dataset.mypcMediaFailed = 'true';
          failedItem.hidden = true;
          const failedThumb = thumbs.find((thumb) => thumb.dataset.mediaThumb === failedItem.dataset.mediaItem);
          if (failedThumb) failedThumb.hidden = true;

          if (failedItem.classList.contains('is-active')) {
            const fallbackThumb = thumbs.find((thumb) => !thumb.hidden && thumb !== failedThumb);
            if (fallbackThumb) fallbackThumb.click();
            else {
              const fallbackItem = items.find((item) => item.dataset.mypcMediaFailed !== 'true');
              fallbackItem?.classList.add('is-active');
            }
          }
          window.requestAnimationFrame(syncActiveMedia);
        });
      });

      const syncActiveMedia = () => {
        const activeItem = getActiveItem();
        if (!activeItem) return;

        const ratio = Math.max(.25, Number(activeItem.dataset.aspectRatio || 1));
        const width = stage.clientWidth || root.clientWidth;
        const viewportHeight = window.visualViewport?.height || window.innerHeight || 800;
        const naturalHeight = width / ratio;
        const desktopCap = window.matchMedia('(min-width: 761px)').matches ? 760 : Infinity;
        const maxHeight = Math.min(viewportHeight * maxHeightPercent / 100, desktopCap);
        const targetHeight = Math.max(160, Math.min(naturalHeight, maxHeight));
        const croppedFraction = naturalHeight > 0 ? (naturalHeight - targetHeight) / naturalHeight : 0;
        const canCropSoftly = verticalFit === 'soft_crop' && ratio < .8 && croppedFraction > 0 && croppedFraction <= .08;

        items.forEach((item) => {
          const video = $('[data-mypc-main-video]', item);
          const isActive = item === activeItem;
          item.classList.toggle('mypc-product-video--soft-crop', isActive && canCropSoftly);
          if (!video) return;
          if (isActive && galleryVisible && !document.hidden) safelyPlay(video);
          else video.pause();
        });

        if (reducedMotion() || !stage.style.height) {
          stage.style.height = `${targetHeight}px`;
        } else {
          window.requestAnimationFrame(() => { stage.style.height = `${targetHeight}px`; });
        }
      };

      root.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-media-thumb]');
        if (thumb) {
          window.requestAnimationFrame(syncActiveMedia);
          return;
        }

        const videoOpen = event.target.closest('[data-mypc-video-open]');
        if (!videoOpen || suppressVideoOpen || root.dataset.mypcVideoLightbox !== 'true') return;
        const video = $('[data-mypc-main-video]', videoOpen);
        openLightbox(video, videoOpen);
      });

      root.addEventListener('keydown', (event) => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target.closest('[data-mypc-video-open]') && root.dataset.mypcVideoLightbox === 'true') {
          event.preventDefault();
          openLightbox($('[data-mypc-main-video]', event.target.closest('[data-mypc-video-open]')), event.target.closest('[data-mypc-video-open]'));
        }
      });

      stage.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) return;
        swipeStart = { x: event.clientX, y: event.clientY, time: performance.now() };
      }, { passive: true });

      stage.addEventListener('pointerup', (event) => {
        if (!swipeStart || !event.isPrimary) return;
        const deltaX = event.clientX - swipeStart.x;
        const deltaY = event.clientY - swipeStart.y;
        const elapsed = performance.now() - swipeStart.time;
        swipeStart = null;
        if (elapsed > 900 || Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

        const availableItems = items.filter((item) => item.dataset.mypcMediaFailed !== 'true');
        const activeItem = getActiveItem();
        const activeIndex = availableItems.indexOf(activeItem);
        const nextIndex = deltaX < 0 ? activeIndex + 1 : activeIndex - 1;
        if (nextIndex < 0 || nextIndex >= availableItems.length) return;
        const nextItem = availableItems[nextIndex];
        const nextThumb = thumbs.find((thumb) => thumb.dataset.mediaThumb === nextItem.dataset.mediaItem);
        suppressVideoOpen = true;
        nextThumb?.click();
        window.setTimeout(() => { suppressVideoOpen = false; }, 80);
      }, { passive: true });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          galleryVisible = entries[0]?.isIntersecting ?? true;
          syncActiveMedia();
        }, { threshold: .12 });
        observer.observe(root);
      }

      const onResize = () => {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(syncActiveMedia);
      };
      window.addEventListener('resize', onResize, { passive: true });
      window.visualViewport?.addEventListener('resize', onResize, { passive: true });
      document.addEventListener('visibilitychange', syncActiveMedia);
      document.addEventListener('variant:change', () => window.requestAnimationFrame(syncActiveMedia));
      syncActiveMedia();
    });
  };

  const initializeBuyNow = (scope = document) => {
    $$('[data-product-root]', scope).forEach((root) => {
      const button = $('[data-mypc-buy-now]', root);
      if (!button || button.dataset.mypcInitialized === 'true') return;
      const variantsNode = $('[data-product-variants]', root);
      const form = $('form.product-form', root);
      const idInput = form && $('input[name="id"]', form);
      const title = $('[data-mypc-buy-now-title]', button);
      const configuration = $('[data-mypc-buy-now-configuration]', button);
      const status = $('[data-mypc-buy-now-status]', root);
      if (!variantsNode || !idInput) return;

      let variants;
      try { variants = JSON.parse(variantsNode.textContent); }
      catch (error) { return; }
      button.dataset.mypcInitialized = 'true';

      const currentVariant = () => variants.find((variant) => String(variant.id) === String(idInput.value));
      const selectedConfiguration = (variant) => {
        const selected = $$('[data-option-position]:checked', root).map((input) => input.value).filter(Boolean);
        const fallback = (variant?.options || []).filter((value) => value && value !== 'Default Title');
        return (selected.length ? selected : fallback).join(' · ') || 'Configuración estándar';
      };

      const renderState = (variant = currentVariant()) => {
        const available = Boolean(variant?.available && variant.id);
        button.disabled = !available;
        if (title) title.textContent = available ? 'Comprar ahora' : (variant ? 'Variante agotada' : 'Combinación no disponible');
        if (configuration) configuration.textContent = selectedConfiguration(variant);
        if (status && available) {
          status.textContent = '';
          status.classList.remove('is-error');
        }
        return variant;
      };

      root.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-position]')) window.requestAnimationFrame(() => renderState());
      });
      document.addEventListener('variant:change', (event) => renderState(event.detail?.variant || currentVariant()));

      button.addEventListener('click', async () => {
        const variant = renderState();
        if (!variant || !variant.available || String(variant.id) !== String(idInput.value)) {
          if (status) {
            status.textContent = 'Selecciona una configuración disponible.';
            status.classList.add('is-error');
          }
          return;
        }

        const originalTitle = title?.textContent || 'Comprar ahora';
        button.disabled = true;
        if (title) title.textContent = 'Añadiendo al carrito…';
        if (status) {
          status.textContent = '';
          status.classList.remove('is-error');
        }

        try {
          const shopRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
          const response = await fetch(`${shopRoot}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: 1 }] })
          });
          const responseData = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(responseData.description || 'No pudimos agregar esta configuración.');
          window.location.assign(`${shopRoot}cart`);
        } catch (error) {
          if (title) title.textContent = originalTitle;
          renderState(variant);
          if (status) {
            status.textContent = error.message || 'Ocurrió un error. Intenta nuevamente.';
            status.classList.add('is-error');
          }
        }
      });

      renderState();
    });
  };

  const initializeReviews = (scope = document) => {
    $$('[data-mypc-reviews]', scope).forEach((root) => {
      if (root.dataset.mypcInitialized === 'true') return;
      root.dataset.mypcInitialized = 'true';

      $('[data-mypc-reviews-show-more]', root)?.addEventListener('click', (event) => {
        $$('[data-mypc-review-hidden]', root).forEach((card) => {
          card.hidden = false;
          card.removeAttribute('data-mypc-review-hidden');
        });
        event.currentTarget.closest('.mypc-product-reviews__more')?.remove();
      });

      root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-mypc-review-video-load]');
        if (!button) return;
        const host = button.closest('[data-mypc-review-video-host]');
        const template = host && $('[data-mypc-review-video-template]', host);
        if (!host || !template) return;

        $$('[data-mypc-reviews] video').forEach((video) => video.pause());
        const fragment = template.content.cloneNode(true);
        const video = $('video', fragment);
        host.replaceChildren(fragment);
        video?.play()?.catch(() => {
          // La reseña conserva sus controles si el navegador exige otro toque.
        });
      });
    });
  };

  const shuffle = (items) => {
    const result = [...items];
    for (let index = result.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
    }
    return result;
  };

  const initializePopups = (scope = document) => {
    $$('[data-mypc-review-popups]', scope).forEach((root) => {
      if (root.dataset.mypcInitialized === 'true') return;
      const items = $$('[data-mypc-review-popup-item]', root);
      if (!items.length) return;
      root.dataset.mypcInitialized = 'true';

      const interval = Math.max(5, Number(root.dataset.mypcPopupInterval || 13)) * 1000;
      const duration = Math.max(4, Number(root.dataset.mypcPopupDuration || 6)) * 1000 + 2500;
      const cooldown = Math.max(30, Number(root.dataset.mypcPopupCooldown || 90)) * 1000;
      const isProduct = root.dataset.mypcPopupPage === 'product';
      const cooldownStorageKey = 'mypcReviewPopupCooldownUntil';
      let order = shuffle(items);
      let cursor = 0;
      let current = null;
      let last = null;
      let showTimer = null;
      let hideTimer = null;
      let dragState = null;
      let suppressClickUntil = 0;

      const storedCooldownUntil = () => {
        try { return Number(window.sessionStorage.getItem(cooldownStorageKey) || 0); }
        catch (error) { return 0; }
      };

      const productControlsAreVisible = () => {
        if (!isProduct) return false;
        const popupWidth = root.getBoundingClientRect().width || Math.min(355, window.innerWidth - 28);
        const onRight = root.classList.contains('mypc-review-popups--bottom_right');
        const bottomOffset = Number.parseFloat(window.getComputedStyle(root).bottom) || 14;
        const popupZone = {
          left: onRight ? window.innerWidth - popupWidth - 14 : 14,
          right: onRight ? window.innerWidth - 14 : popupWidth + 14,
          top: window.innerHeight - bottomOffset - 220,
          bottom: window.innerHeight - bottomOffset
        };
        return $$('[data-mypc-buy-now], [data-add-to-cart], .variant-pills').some((control) => {
          const rect = control.getBoundingClientRect();
          return rect.right > popupZone.left && rect.left < popupZone.right && rect.bottom > popupZone.top && rect.top < popupZone.bottom;
        });
      };

      const schedule = (delay) => {
        window.clearTimeout(showTimer);
        showTimer = window.setTimeout(showNext, delay);
      };

      const finishHide = (item, nextDelay) => {
        $$('[data-mypc-review-popup-video]', item).forEach((video) => video.pause());
        item.hidden = true;
        item.setAttribute('aria-hidden', 'true');
        item.classList.remove('is-visible', 'is-closing', 'is-swipe-closing', 'is-dragging');
        item.style.removeProperty('transform');
        item.style.removeProperty('opacity');
        if (current === item) current = null;
        schedule(nextDelay);
      };

      const hideCurrent = (manual = false, swiped = false) => {
        if (!current) return;
        const item = current;
        if (manual) {
          try { window.sessionStorage.setItem(cooldownStorageKey, String(Date.now() + cooldown)); }
          catch (error) { /* El popup sigue cerrándose aunque el storage esté bloqueado. */ }
        }
        window.clearTimeout(hideTimer);
        $$('[data-mypc-review-popup-video]', item).forEach((video) => video.pause());
        item.classList.remove('is-dragging');
        item.style.removeProperty('transform');
        item.style.removeProperty('opacity');
        item.classList.remove('is-visible');
        item.classList.add(swiped ? 'is-swipe-closing' : 'is-closing');
        window.setTimeout(() => finishHide(item, manual ? cooldown : interval), reducedMotion() ? 0 : 420);
      };

      function showNext() {
        if (document.hidden) {
          schedule(interval);
          return;
        }

        const remainingCooldown = storedCooldownUntil() - Date.now();
        if (remainingCooldown > 0) {
          schedule(remainingCooldown);
          return;
        }

        const blockingPopup = isProduct && ($('.mypc-reminder.is-visible, [data-mypc-video-lightbox-dialog].is-open') || productControlsAreVisible());
        if (blockingPopup) {
          schedule(Math.min(5000, interval));
          return;
        }

        if (cursor >= order.length) {
          order = shuffle(items);
          if (order.length > 1 && order[0] === last) [order[0], order[1]] = [order[1], order[0]];
          cursor = 0;
        }

        const item = order[cursor++];
        last = item;
        current = item;
        item.hidden = false;
        item.setAttribute('aria-hidden', 'false');
        window.requestAnimationFrame(() => item.classList.add('is-visible'));
        const popupVideo = $('[data-mypc-review-popup-video]', item);
        if (popupVideo) {
          popupVideo.currentTime = 0;
          popupVideo.play().catch(() => {
            // Algunos navegadores pueden reservar la reproducción hasta que el popup sea visible.
          });
        }
        window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(() => hideCurrent(false), duration);
      }

      const finishDrag = (event, cancelled = false) => {
        if (!dragState) return;
        const state = dragState;
        dragState = null;
        const deltaX = event.clientX - state.startX;
        const deltaY = event.clientY - state.startY;
        state.item.classList.remove('is-dragging');
        state.item.style.removeProperty('transform');
        state.item.style.removeProperty('opacity');

        if (!cancelled && deltaX < -52 && Math.abs(deltaX) > Math.abs(deltaY) * 1.1) {
          suppressClickUntil = Date.now() + 550;
          hideCurrent(true, true);
        }
      };

      root.addEventListener('pointerdown', (event) => {
        const item = event.target.closest('[data-mypc-review-popup-item]');
        if (!item || item !== current || event.target.closest('[data-mypc-review-popup-close]')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        dragState = { item, startX: event.clientX, startY: event.clientY };
        item.setPointerCapture?.(event.pointerId);
      });

      root.addEventListener('pointermove', (event) => {
        if (!dragState) return;
        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;
        if (deltaX >= 0 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        event.preventDefault();
        dragState.item.classList.add('is-dragging');
        dragState.item.style.transform = `translateX(${deltaX}px)`;
        dragState.item.style.opacity = String(Math.max(.22, 1 - Math.abs(deltaX) / 210));
      });

      root.addEventListener('pointerup', finishDrag);
      root.addEventListener('pointercancel', (event) => finishDrag(event, true));

      root.addEventListener('click', (event) => {
        if (Date.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);

      root.addEventListener('click', (event) => {
        const close = event.target.closest('[data-mypc-review-popup-close]');
        if (!close) return;
        event.preventDefault();
        event.stopPropagation();
        hideCurrent(true);
      });

      schedule(interval + (isProduct ? 700 : 0));
    });
  };

  const initialize = (scope = document) => {
    initializeLightbox();
    initializeProductMedia(scope);
    initializeBuyNow(scope);
    initializeReviews(scope);
    initializePopups(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeLightbox($('[data-mypc-video-lightbox-dialog].is-open'));
  });
})();
